/**
 * O3 — Telemetry feed: scrolling event log of significant battle moments.
 *
 * Bottom-center anchored DOM panel. Renders the last N events up to
 * the current tick, newest at the top. Spammy events (weapon_fire,
 * weapon_miss, "other" fall-through) are filtered out by default —
 * the visible scene already telegraphs those via tracers + dust puffs.
 *
 * Severity drives the row tint: alert (kill / cookoff / breach) gets
 * the danger accent, warn (crits, knockdown, stagger) gets the warn
 * token, info is muted.
 *
 * Layout: a vertical stack of single-line entries with a fade mask at
 * the bottom so the feed visually decays into the scene. Compact
 * enough to coexist with the reasoning panels (which anchor to the
 * bottom corners — the feed sits between them).
 */

import type { CSSProperties } from "react";
import { Panel } from "./Panel";
import type { TelemetryLine, TelemetrySeverity } from "./telemetry";

export interface TelemetryFeedProps {
  /** Pre-formatted rows, newest first. The MatchOverlay derives these
   *  via `telemetryRows(stream, tick, maxRows)`. */
  rows: ReadonlyArray<TelemetryLine>;
  /** Bottom offset in px. */
  bottomOffset?: number;
  /** Override the panel width (defaults to a token). */
  width?: string;
  /** Hide entirely. */
  hidden?: boolean;
}

const rootStyle = (bottomOffset: number, width?: string): CSSProperties => ({
  position: "absolute",
  bottom: bottomOffset,
  left: "50%",
  transform: "translateX(-50%)",
  width: width ?? "var(--pp-telemetry-w)",
  pointerEvents: "none",
  userSelect: "none",
});

const contentStyle: CSSProperties = {
  padding: "var(--pp-padding)",
  display: "flex",
  flexDirection: "column",
  gap: 3,
  color: "var(--pp-text)",
  fontFamily: "var(--pp-font-family)",
  fontSize: "var(--pp-font-md)",
  fontWeight: "var(--pp-weight-normal)",
};

const headerStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingBottom: 4,
  borderBottom: "1px solid var(--pp-border)",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
  marginBottom: 4,
};

const rowStackStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  // Fade mask so older entries decay into the scene rather than abruptly
  // ending. Coordinates with the row opacity ramp below.
  maskImage: "linear-gradient(to bottom, #000 0, #000 70%, transparent 100%)",
  WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 70%, transparent 100%)",
};

const rowStyle = (severity: TelemetrySeverity, opacity: number): CSSProperties => ({
  display: "grid",
  gridTemplateColumns: "auto auto auto 1fr",
  gap: 8,
  alignItems: "baseline",
  padding: "1px 0",
  opacity,
  fontSize: "var(--pp-font-md)",
  color: severityColor(severity),
  fontVariantNumeric: "tabular-nums",
});

const tickStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  letterSpacing: "var(--pp-tracking-caps)",
  minWidth: 36,
};

const sideTagStyle = (side: "A" | "B" | "—"): CSSProperties => ({
  fontSize: "var(--pp-font-sm)",
  fontWeight: "var(--pp-weight-bold)",
  color:
    side === "A"
      ? "var(--pp-accent-a)"
      : side === "B"
        ? "var(--pp-accent-b)"
        : "var(--pp-text-muted)",
  letterSpacing: "var(--pp-tracking-caps)",
  minWidth: 12,
});

const verbStyle: CSSProperties = {
  fontWeight: "var(--pp-weight-bold)",
  letterSpacing: "var(--pp-tracking-caps)",
  minWidth: 60,
};

const detailStyle: CSSProperties = {
  color: "var(--pp-text-muted)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const emptyStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  fontStyle: "italic",
  textAlign: "center",
  padding: "8px 0",
};

function severityColor(s: TelemetrySeverity): string {
  switch (s) {
    case "alert":
      return "var(--pp-danger)";
    case "warn":
      return "var(--pp-warn)";
    case "info":
      return "var(--pp-text)";
  }
}

/**
 * Default bottom offset matches the chrome edge-gutter (12px). Consumers
 * can pass a specific value when stacking the feed above other
 * bottom-anchored panels.
 */
const DEFAULT_BOTTOM_OFFSET = 12;

export function TelemetryFeed({
  rows,
  bottomOffset = DEFAULT_BOTTOM_OFFSET,
  width,
  hidden = false,
}: TelemetryFeedProps) {
  if (hidden) return null;
  return (
    <div style={rootStyle(bottomOffset, width)} aria-label="Telemetry feed">
      <Panel>
        <div style={contentStyle}>
          <div style={headerStyle}>
            <span>TELEMETRY</span>
            <span>LIVE</span>
          </div>
          {rows.length === 0 ? (
            <div style={emptyStyle}>—</div>
          ) : (
            <div style={rowStackStyle}>
              {rows.map((row, i) => {
                // Linear opacity ramp so newer rows are louder. Floor
                // at 0.35 so even the bottom of the visible stack is
                // readable; the mask fade does the rest.
                const opacity = 1 - (i / Math.max(rows.length, 1)) * 0.55;
                return (
                  <div
                    key={`${row.tick}-${i}-${row.verb}`}
                    style={rowStyle(row.severity, opacity)}
                  >
                    <span style={tickStyle}>T{row.tick.toString().padStart(2, "0")}</span>
                    <span style={sideTagStyle(row.side)}>{row.side}</span>
                    <span style={verbStyle}>{row.verb}</span>
                    <span style={detailStyle}>{row.detail}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
